"use client";

import React, { useEffect, useState } from "react";
import { useSearchParams } from "next/navigation";

import EcardsHero from "@/app/home/EcardsHero";
import {
  RIBBON_SURFACE,
  getActiveHolidayBanner,
} from "@/app/home/SeasonalLandingBanner";

const STORAGE_KEY = "ecards-banner-collapsed";

interface EcardsNarrativeBannerProps {
  onStartCreating?: () => void;
}

export default function EcardsNarrativeBanner({
  onStartCreating,
}: EcardsNarrativeBannerProps) {
  const searchParams = useSearchParams();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Pick up the active holiday's background so this banner stays
  // visually continuous with SeasonalLandingBanner above. Falls back
  // to brand purple outside any holiday window. Holiday windows are
  // days wide, so the (extremely rare) midnight-boundary hydration
  // mismatch isn't worth guarding against.
  const headerBgClass =
    getActiveHolidayBanner()?.bgClassName ?? "bg-wg-purple";

  // Initialize state from localStorage and URL params
  useEffect(() => {
    if (typeof window === "undefined") return;

    const storedValue = localStorage.getItem(STORAGE_KEY);
    const hasCategory = searchParams.get("category");
    const hasSearch = searchParams.get("search");

    // Collapse if URL params present or localStorage says collapsed
    if (hasCategory || hasSearch || storedValue === "true") {
      setIsCollapsed(true);
    }

    setIsInitialized(true);
  }, [searchParams]);

  // Update collapsed state when URL params change
  useEffect(() => {
    if (!isInitialized || typeof window === "undefined") return;

    const hasCategory = searchParams.get("category");
    const hasSearch = searchParams.get("search");

    if (hasCategory || hasSearch) {
      setIsCollapsed(true);
      localStorage.setItem(STORAGE_KEY, "true");
    }
  }, [searchParams, isInitialized]);

  const handleStartCreating = () => {
    setIsCollapsed(true);
    if (typeof window !== "undefined") {
      localStorage.setItem(STORAGE_KEY, "true");
    }
    onStartCreating?.();
  };

  const handleLearnMore = () => {
    setIsCollapsed(false);
    if (typeof window !== "undefined") {
      localStorage.setItem(STORAGE_KEY, "false");
    }
  };

  // Smooth-scroll to the FAQ section (id="faq") on the same page,
  // respecting reduced-motion. Mirrors FathersDayNarrativeBanner.
  const handleFaqAnchorClick = (
    event: React.MouseEvent<HTMLAnchorElement>,
  ) => {
    event.preventDefault();
    if (typeof window === "undefined") return;
    const target = document.getElementById("faq");
    if (!target) return;
    const prefersReducedMotion = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    target.scrollIntoView({
      behavior: prefersReducedMotion ? "auto" : "smooth",
      block: "start",
    });
  };

  // Collapsed state - minimal banner
  if (isCollapsed) {
    return (
      <section className={`${headerBgClass} text-white`}>
        {/* Matches SeasonalLandingBanner: max-w-6xl wrapper + 20px
            satin-gradient ribbon at the inner-left margin. No bow on
            this banner — the bow lives on the top banner only, as the
            ribbon "enters" the page from there. */}
        <div className="relative mx-auto flex max-w-6xl flex-col items-start justify-between gap-3 py-4 pl-16 pr-6 sm:flex-row sm:items-center">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute bottom-0 left-0 top-0 w-[40px]"
            style={{ background: RIBBON_SURFACE }}
          />
          <h1 className="text-xl font-bold md:text-2xl">
            Animated eCards That Make Moments Magical
          </h1>
          <div className="flex items-center gap-4">
            <button
              onClick={handleLearnMore}
              aria-expanded="false"
              aria-label="Learn more about animated eCards"
              className="flex items-center gap-1 text-sm font-medium text-white/90 transition-colors hover:text-white"
            >
              Learn more
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
            <a
              href="#faq"
              onClick={handleFaqAnchorClick}
              className="text-sm font-medium text-white/90 underline decoration-white/40 underline-offset-4 transition-colors hover:text-white hover:decoration-white"
            >
              FAQs
            </a>
          </div>
        </div>
      </section>
    );
  }

  // Expanded state - full content. The static hero + supporting sections
  // live in EcardsHero (a server-renderable component that is also the
  // Suspense fallback on `/`), so the crawler-visible copy has a single
  // source of truth. Passing `handleStartCreating` renders the CTA as a
  // button that collapses the banner in place.
  return (
    <EcardsHero
      onStartCreating={handleStartCreating}
      onFaqClick={handleFaqAnchorClick}
    />
  );
}
